import React, { useState, useEffect, useCallback } from "react"
import {ChevronLeft, ChevronRight} from 'lucide-react'

interface HeroSliderProps {
  images: Array<{ imageUrl: string; altText: string; caption?: string }>
  autoPlayInterval?: number
}

export function HeroSlider({ images, autoPlayInterval = 6000 }: HeroSliderProps) {
  const [currentIndex, setCurrentIndex] = useState(0)

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % images.length)
  }, [images.length])

  const goToPrevious = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length)
  }, [images.length])

  useEffect(() => {
    if (images.length <= 1) return

    const interval = setInterval(goToNext, autoPlayInterval)
    return () => clearInterval(interval)
  }, [images.length, autoPlayInterval, goToNext])

  if (images.length === 0) return null

  const currentCaption = images[currentIndex]?.caption

  return (
    <div className="w-full">
      {/* Slider Container */}
      <div className="relative w-full rounded-2xl overflow-hidden shadow-2xl" style={{ aspectRatio: '16/7' }}>
        {/* Images */}
        {images.map((image, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-all duration-1000 ease-in-out ${
              index === currentIndex ? "opacity-100 scale-100" : "opacity-0 scale-105"
            }`}
          >
            <img
              src={image.imageUrl}
              alt={image.altText}
              className="w-full h-full object-cover"
            />
          </div>
        ))}

        {/* Gradient overlay bottom for text readability */}
        {currentCaption && (
          <div className="absolute bottom-0 left-0 right-0 h-1/2 bg-gradient-to-t from-black/70 via-black/30 to-transparent pointer-events-none" />
        )}

        {/* Caption Text */}
        {currentCaption && (
          <div className="absolute bottom-6 md:bottom-10 left-0 right-0 z-10 flex justify-center px-6">
            <p className="text-base md:text-xl text-white font-semibold text-center leading-relaxed drop-shadow-lg max-w-3xl transition-all duration-700">
              {currentCaption}
            </p>
          </div>
        )}

        {/* Navigation Arrows */}
        {images.length > 1 && (
          <>
            <button
              onClick={goToPrevious}
              className="absolute left-3 md:left-5 top-1/2 -translate-y-1/2 z-10 bg-white/80 hover:bg-white p-2 md:p-3 rounded-full shadow-lg transition-all hover:scale-110"
              aria-label="Image précédente"
            >
              <ChevronLeft className="w-5 h-5 md:w-6 md:h-6 text-gray-800" />
            </button>

            <button
              onClick={goToNext}
              className="absolute right-3 md:right-5 top-1/2 -translate-y-1/2 z-10 bg-white/80 hover:bg-white p-2 md:p-3 rounded-full shadow-lg transition-all hover:scale-110"
              aria-label="Image suivante"
            >
              <ChevronRight className="w-5 h-5 md:w-6 md:h-6 text-gray-800" />
            </button>
          </>
        )}

        {/* Dots Navigation */}
        {images.length > 1 && (
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 z-10 flex gap-2">
            {images.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`h-2.5 rounded-full transition-all duration-300 ${
                  index === currentIndex
                    ? "bg-white w-8 shadow-md"
                    : "bg-white/50 w-2.5 hover:bg-white/70"
                }`}
                aria-label={`Aller à l'image ${index + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
